/** @format */

import { useState, useEffect } from "react";
import { Helmet } from "fusion-plugin-react-helmet-async";
import { assetUrl } from "fusion-core";
import TqPanelCard from "../components/tqPanelComponents/tqPanelCard";
import { useForm } from "react-hook-form";
import { TAXI_QUOTE_RD_DISPLAY } from "../../../config/config";
import usePanelStore from "../store/PanelStore";
import useQuoteStore from "../store/QuoteStore";
import useBuyStore from "../../buy/store/BuyStore";
import TqPanelUpdate from "../components/tqPanelComponents/tqPanelUpdate";
import Loader from "../../../shared/components/Loader";
import { getSchemeImage } from "../../../shared/utils/schemeImages";
import { TAXI_QUOTE_BRANDREF } from "../../../config/config";
import BuyStep from "../../buy/enums/BuyStep";
import { useNavigate } from "fusion-plugin-react-router";
import useAppStateStore from "../../../shared/store/AppStateStore";
import { HotjarEventNames } from "../../../shared/utils/hotjarEventNames";
import { TrackHotjarEvent } from "../../../shared/utils/trackHotjarEvent";
import QuoteStep from "../enums/QuoteStep";
import { assetsPath } from "../../../shared/utils/assetsPath";
import TqPanelTable from "../components/tqPanelComponents/TqPanelTable";
import TqPanelMemberFeatures from "../components/tqPanelComponents/tqPanelMemberFeatures";
import ExplanationModal from "../components/explanationModal";
import TqPanelUpdateModal from "../components/tqPanelComponents/tqPanelUpdateModal";
import TqPolicyDateTimeModal from "../components/tqPanelComponents/tqPolicyDateTimeModal";

const cardCss = assetUrl("../../../styles/tqPanelCard.css");
const cutomQuoteimg = assetUrl("../../../assets/icons/VectorQuote.svg");
const star = assetUrl("../../../assets/icons/excessStar.svg");

const splitForSpace = (str) => {
  const [new1, new2] = str.split(" ");
  return (
    <>
      <span>{new1}</span>
      <span>{new2}</span>
    </>
  );
};

const sabreSchemes = [1729, 1763];
const excessReductions = [];

const TqPanel = () => {
  const {
    register,
    formState: { errors },
    watch,
    setValue,
    control,
    trigger,
  } = useForm();

  let STATE = useAppStateStore();

  const navigate = useNavigate();
  const [selectedCardIndex, setSelectedCardIndex] = useState(null);
  const [triggerRender, setTriggerRender] = useState(0);
  const brandRef = TAXI_QUOTE_BRANDREF;

  const { get: getPanelData } = usePanelStore();
  const panelQuoteData = getPanelData()?.panelData ?? "";

  const { setObject } = useBuyStore();
  const riskObjectId = useQuoteStore.getState().quote?.risk?.riskObjectId;

  useEffect(() => {
    if (STATE.memberRetry) {
      handleBuyClick(
        null,
        STATE.selectedCardIndex,
        STATE.selectedCard // Use selectedCard instead of selectedCardQuote
      );
      STATE.setMemberRetry(false);
      STATE.setCurrentStep(BuyStep.TB_ADDON_RECOMMENDATIONS);
    }
  }, []);

  useEffect(() => {
    if (panelQuoteData?.insurerResults?.quoted === null) {
      navigate("/blocked-action?type=no-quote");
    }
  }, []);

  const handleCardClick = (index) => {
    setSelectedCardIndex(index);
    setValue("isSelected", index);
    setTriggerRender((prev) => prev + 1);
  };

  useEffect(() => {
    TrackHotjarEvent(HotjarEventNames.TAXI_QUOTE_TQ_PANEL_ENTERED);
  }, []);

  const handleBuyClick = (e, index, quote) => {
    STATE.setSelectedCard && STATE.setSelectedCard(quote); // Use setSelectedCard
    setSelectedCardIndex(index);
    setValue("isSelected", index);
    setValue("companyName", quote.companyName);
    setValue("pricePerYear", quote.pricePerYear);
    setValue("SelectedSchemeTableId", quote.schemeTableId);
    STATE.setSelectedSchemeTableId &&
      STATE.setSelectedSchemeTableId(quote.schemeTableId);

    // Create the request body
    const panelMemberReqBody = {
      RiskObjectId: riskObjectId,
      RiskObjectHistoryId: "1",
      BrandRef: brandRef,
      PreSelectedQuoteAddons: [],
      SelectedSchemeTableId: quote.schemeTableId || "",
      UseSessionData: true,
    };

    const documentsRequestBody = {
      brandRef,
      RiskObjectId: riskObjectId,
      RiskObjectHistoryId: 1,
      documentTagStage: "Quote",
      documentTagCategory: "PreSaleQuoteDocuments",
    };

    // Store it in BuyStore
    setObject("recommendationsData", null);
    setObject("panelMemberReqBody", panelMemberReqBody);
    setObject("documentsRequestBody", documentsRequestBody);
    setValue("panelMemberReqBody", panelMemberReqBody);

    if (!STATE.memberRetry) {
      STATE.setIspolicyModal(true);
    }
    if (STATE.memberRetry) {
      STATE.setActionOnTqPanel("QuoteSelected");
    }

    if (errors.companyName) {
      trigger("companyName");
    }
    TrackHotjarEvent(HotjarEventNames.TAXI_QUOTE_TQ_PANEL_SELECT_QUOTE_CLICKED);
  };

  useEffect(() => {
    if (Object.keys(errors).length > 0) {
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  }, [errors]);

  useEffect(() => {
    STATE.setShowModel(false);
    STATE.setFeaturesModal(false);
    STATE.setIspolicyModal(false);
    STATE.setQuoteLoading(false);
    const buyStore = useBuyStore.getState();
    buyStore.setObject("selectedAddons", []);
  }, []);

  useEffect(() => {
    STATE.setPreviousStep(QuoteStep.TQ_PANEL);
  }, []);

  const userSession = JSON.parse(sessionStorage.getItem("userSession"));
  const NAME = userSession?.forename;

  const handleModel = () => {
    STATE.setShowModel((prevState) => !prevState);
  };

  const onOpen = (quote) => {
    STATE.setSelectedCard(quote);
    STATE.setFeaturesModal(true);
  };

  const openExplanationModal = (heading, panelQuoteData) => {
    STATE.setExplanationData(panelQuoteData);
    STATE.setModalHeading(heading);
    STATE.setIsExplanationModal(true);
  };

  return (
    <>
      <Helmet>
        <link rel="stylesheet" href={cardCss} />
      </Helmet>
      {STATE.quoteLoading && <Loader text="Selecting quote..." />}
      <div className="panelMainDiv">
        <div className="disappear-1023">
          <TqPanelUpdate watch2={watch} STATE={STATE} buySetValue={setValue} />
        </div>
        <div className="CustomizeQuoteDiv">
          <div className="form3 container">
            <h1 className="margin-bottom-10">Choose your quote</h1>
            <p className="margin-bottom-30">
              Thank you, {NAME}. Here are your quotes based on the information
              you have provided. Please note that all policies must be purchased
              and administered online.
            </p>
            {errors.companyName && (
              <span className="errorField">
                Buy a quote to proceed further!
              </span>
            )}
            <button
              onClick={handleModel}
              type="button"
              className="quoteShowStyle"
            >
              <div className="quoteBtnStyle">
                <div className="quoteimgStyle">
                  <img className="imgDesign2" src={cutomQuoteimg} alt="" />
                </div>
                <div className="quoteTextBtnStyle">Customise Quote Details</div>
              </div>
            </button>
            <div className="table-container">
              <div style={{ width: "fit-content" }}>
                <TqPanelTable
                  panelQuoteData={panelQuoteData}
                  sabreSchemes={sabreSchemes}
                  getSchemeImage={getSchemeImage}
                  handleBuyClick={handleBuyClick}
                  onOpen={onOpen}
                  splitForSpace={splitForSpace}
                  watch={watch}
                  star={star}
                  excessReductions={excessReductions}
                />
              </div>
              <div className="quoteDetailsPageStyle">
                <p>
                  All policies must be purchased and administered online and we
                  have a dedicated online team to help you through any queries.
                </p>
                <p>
                  (Please quote your reference number:{" "}
                  {panelQuoteData?.quoteReference ?? ""})
                </p>
                <p>* Please refer to policy details</p>
              </div>
            </div>

            {panelQuoteData?.insurerResults?.quoted &&
            panelQuoteData?.insurerResults?.quoted?.length > 0 ? (
              panelQuoteData?.insurerResults?.quoted?.map((quote, index) => {
                const isSelected = watch("isSelected") === index;
                return (
                  <TqPanelCard
                    key={index}
                    {...quote}
                    STATE={STATE}
                    isSelected={isSelected}
                    onClick={() => handleCardClick(index)}
                    register={register}
                    errors={errors}
                    panelQuoteData={panelQuoteData}
                    watch={watch}
                    control={control}
                    trigger={trigger}
                    setValue={setValue}
                    currentStep={STATE.currentStep}
                    onOpen={onOpen}
                    featuresModal={STATE.featuresModal}
                    setSelectedSchemeTableId={STATE.setSelectedSchemeTableId}
                    openExplanationModal={openExplanationModal}
                    quote={quote}
                    setSelectedCardIndex={setSelectedCardIndex}
                    setSelectedCard={STATE.setSelectedCard} // Use setSelectedCard
                  />
                );
              })
            ) : (
              <div className="appear-1023">
                <h1 className="margin-top-20">
                  Sorry we are unable to provide a price for you based on the
                  information provided
                </h1>
                {TAXI_QUOTE_RD_DISPLAY === true &&
                  panelQuoteData?.insurerResults?.referredOrDeclined?.map(
                    (insurer, index) => {
                      if (
                        insurer.declined &&
                        insurer.declineReasons &&
                        insurer.declineReasons.length > 0
                      ) {
                        return (
                          <div key={`insurer-${index}`}>
                            <h4>{insurer.schemeName}</h4>
                            <ul>
                              {insurer.declineReasons.map((reason, i) => (
                                <li key={i}>{reason}</li>
                              ))}
                            </ul>
                          </div>
                        );
                      } else {
                        return null;
                      }
                    }
                  )}
              </div>
            )}
          </div>
        </div>
      </div>

      <TqPanelMemberFeatures />
      <ExplanationModal />
      <TqPanelUpdateModal />
      {STATE.ispolicyModal && <TqPolicyDateTimeModal />}
    </>
  );
};

export default TqPanel;
